import { gql } from '@graphql/__generated__';
import {
  COMPARABLES_SETTINGS_ANCHOR,
  REPORT_ENABLE_SECTION_ANCHOR,
  REPORT_SELECTION_ACTIONS_ANCHOR,
  TRANSACTIONS_SETTINGS_ANCHOR,
} from '@modules/_shared/components/report-editor/preview/previewAnchors';
import type {
  TourModule,
  TourPathContext,
} from '@modules/product-updates/tours/tourTypes';

const GET_TOUR_CMA_REPORT = gql(/* GraphQL */ `
  query GetCmaTourReport($userId: uuid!) {
    own: cma_reports(
      where: { created_by: { _eq: $userId } }
      order_by: [{ created_at: desc }]
      limit: 1
    ) {
      id
    }
    visible: cma_reports(order_by: [{ created_at: desc }], limit: 1) {
      id
    }
  }
`);

// The user's latest CMA report, else the latest one they can see, opened on
// `pageId`.
const latestReportPage =
  (pageId: string) =>
  async ({ client, userId }: TourPathContext) => {
    const { data } = await client.query({
      query: GET_TOUR_CMA_REPORT,
      variables: { userId },
      fetchPolicy: 'network-only',
    });
    const reportId = data?.own[0]?.id ?? data?.visible[0]?.id;
    return reportId == null ? null : `/cma-reports/${reportId}/${pageId}`;
  };

const RENTAL_PAGE_ID = 'page-rental-comparables-list';

// On the "Comparable rental listings" step (`RentalListings.tsx`). From
// "Show me" on `content/releases/cma-rental-comparables`.
export const cmaRentalComparablesTour: TourModule = {
  steps: t => [
    {
      anchor: REPORT_ENABLE_SECTION_ANCHOR,
      title: t('Turn the section on'),
      body: t(
        'Comparable rentals are off by default. Enable this section to add them and a rent estimate to the report.',
      ),
      placement: 'top',
    },
    {
      anchor: REPORT_SELECTION_ACTIONS_ANCHOR,
      title: t('Fetch comparable rentals'),
      body: t(
        'Automatically fetches up to 10 similar rentals nearby; Manually lets you search and pick them. Their median rent per m² gives the rent estimate.',
      ),
      placement: 'bottom-start',
    },
  ],
  // A section already on has no Enable button: start on the rentals.
  startOnAnyStep: true,
  resolvePath: latestReportPage(RENTAL_PAGE_ID),
};

// On the "Comparable listings" step (`ComparableListingsSettings.tsx`). From
// "Show me" on `content/releases/cma-comparables-cards`.
export const cmaComparablesCardsTour: TourModule = {
  steps: t => [
    {
      anchor: COMPARABLES_SETTINGS_ANCHOR,
      title: t('Cards or table rows'),
      body: t(
        'Open Settings to show comparable listings as cards with a photo and description, or as table rows.',
      ),
      placement: 'bottom-start',
    },
  ],
  resolvePath: latestReportPage('page-comparables-list'),
};

// On the comparable transactions step (`StepTransactions.tsx`). From "Show
// me" on `content/releases/cma-dvf-transactions` (French properties).
export const cmaDvfTransactionsTour: TourModule = {
  steps: t => [
    {
      anchor: REPORT_SELECTION_ACTIONS_ANCHOR,
      title: t('Sales from two sources'),
      body: t(
        'Automatically picks the closest sales from DVF and RealAdvisor transactions; Manually lets you search both.',
      ),
      placement: 'bottom-start',
    },
    {
      anchor: TRANSACTIONS_SETTINGS_ANCHOR,
      title: t("Today's prices"),
      body: t(
        "In Settings, Adjust price by evolution brings each sale price up to today's market.",
      ),
      placement: 'bottom-start',
    },
  ],
  resolvePath: latestReportPage('page-transactions'),
};
